import { EPaymentPlan } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import { BodyText, StrongValue, SubsectionTitle, useCan } from '@if/mui'
import { access } from '@src/access'
import { CircularProgress, Stack } from '@mui/material'
import { mustShowInterestRate } from '@src/data/creditapp-selectors'
import {
  FormatPaymentPlanForSummary,
  getCoverageTotal,
  getIfXpressTotalObligation,
  getMerchantFees,
  getSelectedPlan,
} from '@src/data/worksheet-selectors'
import { ComputeFundingResultDto } from '@src/types/ComputeIfXpressFundingResultDto'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useTranslation } from 'react-i18next'

interface RowProbs {
  label: string
  value: string
}

const RowStack = ({ label, value }: RowProbs) => {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <BodyText>{label} :</BodyText>
      <BodyText>{value}</BodyText>
    </Stack>
  )
}

interface Props {
  creditApplication: CreditApplication
  amountRequested: number
  hasVariableInterest: boolean
  firstPaymentOn: string
  merchant: Merchant
  paymentPlanId: EPaymentPlan
  computedIfXpressFunding: ComputeFundingResultDto | null
  isComputing: boolean
}
const IfXpressFundingSummary = ({
  creditApplication,
  amountRequested,
  hasVariableInterest,
  firstPaymentOn,
  merchant,
  paymentPlanId,
  computedIfXpressFunding,
  isComputing,
}: Props) => {
  const { t } = useTranslation()
  const totalObligation = getIfXpressTotalObligation(amountRequested, computedIfXpressFunding)
  const selectedPlan = getSelectedPlan(merchant, paymentPlanId)
  const showRate = useCan(access.showRate)
  const mustDisplayInterests = mustShowInterestRate(creditApplication, merchant, showRate)
  const insuranceTotal = getCoverageTotal(computedIfXpressFunding)

  return (
    <Stack direction="column" spacing={2}>
      <SubsectionTitle>{t('worksheet.fundingSummary')} :</SubsectionTitle>
      {isComputing && (
        <div style={{ textAlign: 'center' }}>
          <CircularProgress />
        </div>
      )}
      {!computedIfXpressFunding && !isComputing && <BodyText>{t('worksheet.personalFundingInvalid')}</BodyText>}
      {computedIfXpressFunding && (
        <>
          <RowStack label={t('worksheet.requestedAmount')} value={formatCurrency(amountRequested)} />
          {mustDisplayInterests && (
            <>
              <RowStack
                label={`${t('worksheet.interest')} (${
                  hasVariableInterest
                    ? FormatPaymentPlanForSummary(selectedPlan)
                    : `${creditApplication.finalCreditDecision.interestRate}%`
                })`}
                value={formatCurrency(computedIfXpressFunding.totalInterestAmount)}
              />

              <RowStack
                label={`${t('worksheet.financeFee')} (${computedIfXpressFunding.lenderFeeRate}%)`}
                value={formatCurrency(computedIfXpressFunding.lenderFee)}
              />

              {hasVariableInterest && (
                <RowStack
                  label={`${t('worksheet.merchantFee')} (${selectedPlan?.merchantFeeRate}%)`}
                  value={getMerchantFees(selectedPlan?.merchantFeeRate, amountRequested)}
                />
              )}

              <RowStack label={t('worksheet.protectionPlan')} value={formatCurrency(insuranceTotal)} />

              <RowStack label={t('worksheet.totalObligation')} value={formatCurrency(totalObligation)} />

              <RowStack
                label={t('worksheet.annualPercentageRate')}
                value={`${computedIfXpressFunding?.effectiveRate}%`}
              />
            </>
          )}

          <Stack direction="row" justifyContent="space-between" paddingTop={3}>
            <SubsectionTitle>{t('worksheet.paymentTitle')}</SubsectionTitle>
          </Stack>

          <RowStack
            label={t('worksheet.maxPmtAmount')}
            value={formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}
          />

          <RowStack label={t('worksheet.paymentFrequency')} value={t('common.monthly')} />

          <RowStack
            label={t('worksheet.monthlyPayment')}
            value={formatCurrency(computedIfXpressFunding?.paymentForFrequency)}
          />

          <RowStack label={t('worksheet.firstPaymentOn')} value={formatDate(firstPaymentOn)} />

          <Stack direction="row" justifyContent="space-between" paddingTop={3}>
            <StrongValue>{t('worksheet.amountToBePaid')} :</StrongValue>
            <BodyText>{formatCurrency(amountRequested ?? 0)}</BodyText>
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default IfXpressFundingSummary
